import curry from "../../lib/core/functional/curry";
import noop from "../../lib/core/functional/noop";
import loadPreactModule from "../../utils/loadPreactModule";
import renderPreactWithProps from "../../utils/renderPreactWithProps";
import loadExternalStylesheet from "../../utils/loadExternalStylesheet";
import callIfArgumentsPass from "../../utils/callIfArgumentsPass";

function didUserInfoChanged(prevErr, prevUserInfo, err, userInfo) {
    if (prevErr != err) {
        return true;
    }

    return userInfo.menu.length !== prevUserInfo.menu.length;
}

function findGroupSite(site) {
    return site.type_name.toLowerCase() === "group";
}

function makePermisionMap(userInfo) {
    const { sites, runnerSite, profileSite } = userInfo;
    const communitySite = sites.find(findGroupSite) || { permissions: {} };
    return {
        parent_site: runnerSite.permissions,
        user_site: profileSite.permissions,
        community_site: communitySite,
    };
}

const doesUserHavePermission = curry(function doesUserHavePermission(
    permissionMap,
    requiredPermission
) {
    const { on, permission } = requiredPermission;
    return permission in (permissionMap[on] || {});
});

function sortByOrder(entryA, entryB) {
    return entryA.order - entryB.order;
}

function makeRenderInfo(userInfo) {
    const site_name = userInfo.profileSite ? userInfo.profileSite.name : "";
    const parent_id = userInfo.runnerSite ? userInfo.runnerSite.id : 0;
    const id = userInfo.profileSite
        ? userInfo.profileSite.id
        : userInfo.runnerSite
        ? userInfo.runnerSite.id
        : window.REBELMOUSE_BOOTSTRAP_DATA.runner.id;
    const right_site = (
        (userInfo.runnerSite.permissions.edit_post || userInfo.runnerSite.permissions.create_draft
            ? userInfo.runnerSite
            : userInfo.profileSite) || {}
    ).id;
    const user_site_title = userInfo.profileSite ? userInfo.profileSite.title : "";
    const user_site_avatar = userInfo.profileSite ? userInfo.profileSite.display_logo : "";

    return {
        site_name,
        parent_id,
        id,
        right_site,
        user_site_title,
        user_site_avatar,
    };
}

function resolvePotentialTemplate(renderInfo, str) {
    if (!str) {
        return str;
    }
    return str
        .replace(/{{right_site}}/gi, renderInfo.right_site)
        .replace(/{{id}}/gi, renderInfo.id)
        .replace(/{{site_name}}/gi, renderInfo.site_name)
        .replace(/{{parent_id}}/gi, renderInfo.parent_id)
        .replace(/{{user_site_title}}/gi, renderInfo.user_site_title)
        .replace(/{{user_site_avatar}}/gi, renderInfo.user_site_avatar);
}
const menuEntryReducer = curry(function _menuEntryReducer(
    permissionMap,
    renderInfo,
    newMenu,
    entry
) {
    if (entry.visibility === false) {
        return newMenu;
    }

    if ("permission_required" in entry) {
        let isAllowed = true;

        if (Array.isArray(entry.permission_required)) {
            isAllowed = !!entry.permission_required.find(doesUserHavePermission(permissionMap));
        } else {
            isAllowed = !!doesUserHavePermission(permissionMap, entry.permission_required);
        }

        if (!isAllowed) {
            return newMenu;
        }
    }

    const newEntry = {
        ...entry,
        url: resolvePotentialTemplate(renderInfo, entry.url),
        title: resolvePotentialTemplate(renderInfo, entry.title),
        logo: resolvePotentialTemplate(renderInfo, entry.logo),
    };

    if ("submenu" in entry && entry.submenu.length > 0) {
        const submenu = entry.submenu.reduce(menuEntryReducer(permissionMap, renderInfo), []);
        if (submenu.length === 0) {
            return newMenu;
        }

        newEntry.submenu = submenu.sort(sortByOrder);
        return [...newMenu, newEntry];
    }

    return [...newMenu, newEntry];
});

export const renderMainMenuButton = curry(function renderMainMenuButton(
    renderModule,
    menuEntries,
    err,
    userInfo
) {
    if (err) {
        renderModule({ entries: [] });
        return;
    }

    const permissionsMap = makePermisionMap(userInfo);
    const urlRenderInfo = makeRenderInfo(userInfo);
    const entries = menuEntries.reduce(menuEntryReducer(permissionsMap, urlRenderInfo), []);
    renderModule({ entries });
});

const onModule = curry(function onModule(context, element, [renderer, Module]) {
    context.listenForUserInfo(
        callIfArgumentsPass(
            didUserInfoChanged,
            renderMainMenuButton(
                renderPreactWithProps(renderer, Module, element),
                window.REBELMOUSE_BOOTSTRAP_DATA.whitelabel_menu || []
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);
loadExternalStylesheet("/static/css-build/roar/runner/modules/menu-global.css", noop);
export default function renderer(context, element) {
    loadPreactModule("MainMenu", context, {}, internalLoader).then(onModule(context, element));
}
